import Code20 from '@carbon/icons-react/es/code/20';
import OverflowMenuHorizontal16 from '@carbon/icons-react/es/overflow-menu--horizontal/16';
import PageBreak20 from '@carbon/icons-react/es/page-break/20';
import TextBold20 from '@carbon/icons-react/es/text--bold/20';
import TextItalic20 from '@carbon/icons-react/es/text--italic/20';
import TextStrikethrough20 from '@carbon/icons-react/es/text--strikethrough/20';
import TextUnderline20 from '@carbon/icons-react/es/text--underline/20';
import React from 'react';
import { Button } from 'components/atoms/button';
import { Popover } from 'components/atoms/popover';
import { VariablesSelect } from 'components/molecules/variables/variables-select';
import { INSERT_EMOJI_COMMAND } from 'vendor/ckeditor/plugins/emoji/constants';
import { AddList } from '../add-list';
import { AIFillSelectorTrigger } from '../ai-fill/ai-fill-selector-trigger';
import { ChangeAlignment } from '../change-alignment';
import { EmojiPicker } from '../emoji-picker/emoji-picker';
import { HighlightText } from '../highlight';
import { ImageUploader } from '../image-uploader';
import { InsertBlockEls } from '../insert-block-els';
import { InsertPanels } from '../insert-panels';
import { InsertTable } from '../insert-table';
import { TextColor } from '../text-color';
import { ToolsRenderer } from './toolbar';
import { BlockToolsGroup } from './types';

export const statusUpdateMoreTools = [
  {
    group: 'group-1',
    items: [
      {
        key: 'strikethrough',
        label: 'toolbar.strikethrough',
        Component: TextStrikethrough20,
        keyStroke: 'CTRL+SHIFT+X',
      },
      {
        key: 'code',
        label: 'toolbar.code',
        Component: Code20,
      },
      {
        key: 'insertImage',
        label: 'toolbar.insert_image',
        Component: ImageUploader,
        haveItsOwnHandler: true,
      },
      {
        key: INSERT_EMOJI_COMMAND,
        label: '',
        // Emoji picker have its own tooltip
        Component: EmojiPicker,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertTable',
        label: 'toolbar.insert_table',
        Component: InsertTable,
        haveItsOwnHandler: true,
      },
    ],
  },
];

const StatusUpdateMoreTools = ({ editor }) => {
  return (
    <Popover
      trigger="hover"
      placement="bottom"
      content={<ToolsRenderer tools={statusUpdateMoreTools} editor={editor} />}
    >
      <Button
        style={{ fontSize: 'var(--font-size-sm)' }}
        type="text"
        icon={<OverflowMenuHorizontal16 />}
      />
    </Popover>
  );
};

// Places other than `haveItsOwnHandler` is set, we're using key as the command.
// The key should match the command as we're enable/disable the button based on the key
export const tools = [
  {
    group: 'text-tools',
    items: [
      {
        key: 'bold',
        label: 'toolbar.bold',
        Component: TextBold20,
        keyStroke: 'CTRL+B',
      },
      {
        key: 'italic',
        label: 'toolbar.italic',
        Component: TextItalic20,
        keyStroke: 'CTRL+I',
      },
      {
        key: 'underline',
        label: 'toolbar.underline',
        Component: TextUnderline20,
        keyStroke: 'CTRL+U',
      },
      {
        key: 'color',
        label: 'toolbar.font_color',
        Component: TextColor,
        haveItsOwnHandler: true,
      },
      {
        key: 'fontBackgroundColor',
        label: 'toolbar.highlight',
        Component: HighlightText,
        haveItsOwnHandler: true,
      },
      {
        key: 'strikethrough',
        label: 'toolbar.strikethrough',
        Component: TextStrikethrough20,
        keyStroke: 'CTRL+SHIFT+X',
      },
      {
        key: 'code',
        label: 'toolbar.code',
        Component: Code20,
      },
      {
        key: 'list',
        label: 'toolbar.list',
        Component: AddList,
        haveItsOwnHandler: true,
      },
      {
        key: 'align',
        label: 'toolbar.alignment',
        Component: ChangeAlignment,
        haveItsOwnHandler: true,
      },
    ],
  },
  {
    group: 'block-tools',
    items: [
      {
        key: 'insertImage',
        label: 'toolbar.insert_image',
        Component: ImageUploader,
        haveItsOwnHandler: true,
      },
      {
        key: INSERT_EMOJI_COMMAND,
        label: '',
        // Emoji picker have its own tooltip
        Component: EmojiPicker,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertTable',
        label: 'toolbar.insert_table',
        Component: InsertTable,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertPanels',
        label: '',
        Component: InsertPanels,
        haveItsOwnHandler: true,
      },
      {
        key: BlockToolsGroup.InsertBlockElsKey,
        label: '',
        Component: InsertBlockEls,
        haveItsOwnHandler: true,
      },
      {
        key: 'pageBreak',
        label: 'toolbar.page_break',
        Component: PageBreak20,
      },
    ],
  },
  {
    group: 'app-tools',
    items: [
      {
        key: 'ai-fill',
        label: 'toolbar.ai_fill',
        Component: AIFillSelectorTrigger,
        haveItsOwnHandler: true,
      },
    ],
  },
];

export const statusUpdateTools = [
  {
    group: 'text-tools',
    items: [
      {
        key: 'bold',
        label: 'toolbar.bold',
        Component: TextBold20,
        keyStroke: 'CTRL+B',
      },
      {
        key: 'italic',
        label: 'toolbar.italic',
        Component: TextItalic20,
        keyStroke: 'CTRL+I',
      },
      {
        key: 'underline',
        label: 'toolbar.underline',
        Component: TextUnderline20,
        keyStroke: 'CTRL+U',
      },
      {
        key: 'color',
        label: 'toolbar.font_color',
        Component: TextColor,
        haveItsOwnHandler: true,
      },
    ],
  },
  {
    group: 'extra-tools',
    items: [
      {
        key: 'list',
        label: 'toolbar.list',
        Component: AddList,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertImage',
        label: 'toolbar.insert_image',
        Component: ImageUploader,
        haveItsOwnHandler: true,
      },

      {
        key: 'insertTable',
        label: 'toolbar.insert_table',
        Component: InsertTable,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertVariable',
        label: 'toolbar.variables',
        Component: VariablesSelect,
        haveItsOwnHandler: true,
        componentProps: {
          btnClassName: 'smartfill-icon',
        },
      },
    ],
  },
  {
    group: 'status-update-app-tools',
    items: [
      {
        key: 'ai-fill',
        label: 'toolbar.ai_fill',
        Component: AIFillSelectorTrigger,
        haveItsOwnHandler: true,
      },
    ],
  },
];

export const documentTemplateTools = [
  {
    group: 'group-1',
    items: [
      {
        key: 'bold',
        label: 'toolbar.bold',
        Component: TextBold20,
        keyStroke: 'CTRL+B',
      },
      {
        key: 'italic',
        label: 'toolbar.italic',
        Component: TextItalic20,
        keyStroke: 'CTRL+I',
      },
      {
        key: 'underline',
        label: 'toolbar.underline',
        Component: TextUnderline20,
        keyStroke: 'CTRL+U',
      },
      {
        key: 'color',
        label: 'toolbar.font_color',
        Component: TextColor,
        haveItsOwnHandler: true,
      },
      {
        key: 'highlight',
        label: 'toolbar.highlight',
        Component: HighlightText,
        haveItsOwnHandler: true,
      },
      {
        key: 'strikethrough',
        label: 'toolbar.strikethrough',
        Component: TextStrikethrough20,
        keyStroke: 'CTRL+SHIFT+X',
      },
      {
        key: 'code',
        label: 'toolbar.code',
        Component: Code20,
      },
      {
        key: 'list',
        label: 'toolbar.list',
        Component: AddList,
        haveItsOwnHandler: true,
      },
      {
        key: 'align',
        label: 'toolbar.alignment',
        Component: ChangeAlignment,
        haveItsOwnHandler: true,
      },
    ],
  },
  {
    group: 'group-2',
    items: [
      {
        key: 'insertTable',
        label: 'toolbar.insert_table',
        Component: InsertTable,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertPanels',
        label: '',
        Component: InsertPanels,
        haveItsOwnHandler: true,
      },
      {
        key: BlockToolsGroup.InsertBlockElsKey,
        label: '',
        Component: InsertBlockEls,
        haveItsOwnHandler: true,
      },
    ],
  },
  {
    group: 'group-3',
    items: [
      {
        key: 'insertVariable',
        label: 'toolbar.variables',
        Component: VariablesSelect,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertImage',
        label: 'toolbar.insert_image',
        Component: ImageUploader,
        haveItsOwnHandler: true,
      },
      {
        key: INSERT_EMOJI_COMMAND,
        label: '',
        // Emoji picker have its own tooltip
        Component: EmojiPicker,
        haveItsOwnHandler: true,
      },
      {
        key: 'pageBreak',
        label: 'toolbar.page_break',
        Component: PageBreak20,
      },
    ],
  },
  {
    group: 'app-tools',
    items: [
      {
        key: 'ai-fill',
        label: 'toolbar.ai_fill',
        Component: AIFillSelectorTrigger,
        haveItsOwnHandler: true,
      },
    ],
  },
];

export const statusUpdateHeaderTools = [
  {
    group: 'group-1',
    items: [
      {
        key: 'bold',
        label: 'toolbar.bold',
        Component: TextBold20,
        keyStroke: 'CTRL+B',
      },
      {
        key: 'italic',
        label: 'toolbar.italic',
        Component: TextItalic20,
        keyStroke: 'CTRL+I',
      },
      {
        key: 'underline',
        label: 'toolbar.underline',
        Component: TextUnderline20,
        keyStroke: 'CTRL+U',
      },
      {
        key: 'color',
        label: 'toolbar.font_color',
        Component: TextColor,
        haveItsOwnHandler: true,
      },
    ],
  },
  {
    group: 'group-2',
    items: [
      {
        key: 'list',
        label: 'toolbar.list',
        Component: AddList,
        haveItsOwnHandler: true,
      },
      {
        key: 'align',
        label: 'toolbar.alignment',
        Component: ChangeAlignment,
        haveItsOwnHandler: true,
      },
      {
        key: 'insertVariable',
        label: 'toolbar.variables',
        Component: VariablesSelect,
        haveItsOwnHandler: true,
        componentProps: {
          btnClassName: 'status-update-trigger',
        },
      },
    ],
  },
  {
    group: 'status-update-app-tools',
    items: [
      {
        key: 'ai-fill',
        label: 'toolbar.ai_fill',
        Component: AIFillSelectorTrigger,
        haveItsOwnHandler: true,
      },
    ],
  },
  {
    group: 'group-3',
    items: [
      {
        key: 'moreTools',
        label: 'toolbar.variables',
        Component: StatusUpdateMoreTools,
        haveItsOwnHandler: true,
      },
    ],
  },
];
export const appToolGroupNames = ['status-update-app-tools', 'app-tools'];
